import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import NewShipment from './pages/NewShipment';
import NewIntlShipment from './pages/NewIntlShipment';
import AddressBook from './pages/AddressBook';
import References from './pages/References';
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import AllShipments from './pages/AllShipments';
import AccountInfo from './pages/AccountInfo';
import Notifications from './pages/Notifications';
import Reports from './pages/Reports';
import SystemMonitor from './pages/SystemMonitor';
import ChangePassword from './pages/ChangePassword';
import './App.css';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  // Check if user must change password
  if (user?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" state={{ forced: true, from: location.pathname + location.search }} replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  if (!['admin', 'super_admin'].includes(user?.role)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/new-shipment"
          element={
            <PrivateRoute>
              <NewShipment />
            </PrivateRoute>
          }
        />
        <Route
          path="/new-intl-shipment"
          element={
            <PrivateRoute>
              <NewIntlShipment />
            </PrivateRoute>
          }
        />
        <Route
          path="/address-book"
          element={
            <PrivateRoute>
              <AddressBook />
            </PrivateRoute>
          }
        />
        <Route
          path="/references"
          element={
            <PrivateRoute>
              <References />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <UserManagement />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/shipments"
          element={
            <AdminRoute>
              <AllShipments />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <AdminRoute>
              <Reports />
            </AdminRoute>
          }
        />
        <Route
          path="/system-monitor"
          element={
            <AdminRoute>
              <SystemMonitor />
            </AdminRoute>
          }
        />
        <Route
          path="/account"
          element={
            <PrivateRoute>
              <AccountInfo />
            </PrivateRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <PrivateRoute>
              <Notifications />
            </PrivateRoute>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
